import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './TeamCompBenchmarks.css';
import DataService from '../../XXX/DataService';
import { ChampionItemBuildData } from '../../NewWebSocket/components/ChampionInfo/ChampionItemBuild';
import { TeamSlot, Role } from './TeamCompBuilds';
import TeamCompSnapshot, { SnapshotChampionData } from './TeamCompSnapshot';

// ═══════════════════════════════════════════════════════════════════
// Constants — XP / Gold curves
// ═══════════════════════════════════════════════════════════════════

/** Cumulative XP thresholds per level (index = level) */
const LEVEL_XP_TABLE = [
  0, 0, 280, 660, 1140, 1720, 2400, 3180, 4060, 5040,
  6120, 7300, 8580, 9960, 11440, 13020, 14700, 16480, 18360, 20340, 22420,
];

const BASE_XP = { melee: 65, caster: 32, cannon: 100 };

// Matches backend WaveGoldCalculator: solo 0.95 for top, mid, jgl; duo 0.62 for bot, sup
const XP_MULT: Record<string, number> = { solo: 0.95, duo: 0.62 };

const FIRST_WAVE_SPAWN = 65;  // first wave arrives in lane ~1:05
const WAVE_AT_14_MIN = 26;

const STARTING_GOLD = 500;
const MAX_WAVES = 80;
const DEFAULT_CSPM = 10;

const GOLD_DIFF_OPTIONS = [-1500, -1000, -500, 0, 500, 1000, 1500];

// Fixed (non-drake) events
const FIXED_EVENTS: GameEvent[] = [
  { key: 'grubs', label: 'Grubs', sub: '8:00', sec: 480 },
  { key: 'herald', label: 'Herald', sub: '14:00', sec: 840 },
  { key: 'baron', label: 'Baron', sub: '20:00', sec: 1200 },
];

// Drake respawn interval (seconds)
const DRAKE_RESPAWN_SEC = 5 * 60; // 5 minutes
const MAX_DRAKES = 6;

// ── Top lane quest ──
const TOP_QUEST_THRESHOLD = 1200;
const TOP_QUEST_PASSIVE_PER_SEC = 1.6;   // 96 pts/min = 1.6 pts/sec
const TOP_QUEST_PASSIVE_START = 65;       // 1:05
const TOP_QUEST_PTS_PER_CS = 2;
const TOP_QUEST_BONUS_XP = 600;           // immediate XP on completion
const TOP_QUEST_BONUS_XP_PCT = 0.125;     // +12.5% minion XP after completion

function computeTopQuestCompletionSec(): number {
  for (let sec = TOP_QUEST_PASSIVE_START; sec <= 1800; sec++) {
    const passive = TOP_QUEST_PASSIVE_PER_SEC * (sec - TOP_QUEST_PASSIVE_START);
    const csMin = Math.max(0, sec / 60 - 1.5);
    const csPoints = TOP_QUEST_PTS_PER_CS * DEFAULT_CSPM * csMin;
    if (passive + csPoints >= TOP_QUEST_THRESHOLD) return sec;
  }
  return 1800;
}
const TOP_QUEST_COMPLETION_SEC = computeTopQuestCompletionSec();

// Item category sets
const BOOTS_IDS = new Set([
  '1001', '3006', '3009', '3020', '3047', '3111', '3117', '3158',
  '223009', '223020', '223047', '223111', '223158', '223006',
]);
const JUNGLE_STARTER_IDS = new Set(['1101', '1102', '1103']);
const ALL_SUPPORT_ITEM_IDS = new Set([
  '3865', '3866', '3867', '3869', '3870', '3871', '3876', '3877',
]);

// ═══════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════

interface DDragonItem {
  name: string;
  gold: { total: number; base: number; sell: number; purchasable: boolean };
}
type ItemsMap = Record<string, DDragonItem>;

interface SpikeInfo {
  time: string;
  timeSec: number;
  level: number;
  goldNeeded: number;
  items: string[];
}

interface GameEvent {
  key: string;
  label: string;
  sub: string;
  sec: number;
}

type XPDataPoint = { sec: number; xp: number; level: number; fractionalLevel: number };

// ═══════════════════════════════════════════════════════════════════
// XP / Gold utility functions
// ═══════════════════════════════════════════════════════════════════

function getLaneType(role: Role): string {
  if (role === 'bot' || role === 'sup') return 'duo';
  return 'solo'; // top, mid, jgl all use solo XP (same as backend)
}

function getXPMult(role: Role): number {
  return XP_MULT[getLaneType(role)] ?? 0.95;
}

function getWaveSpawnTime(wave: number): number {
  if (wave <= 0) return 0;
  if (wave <= WAVE_AT_14_MIN) return FIRST_WAVE_SPAWN + (wave - 1) * 30;
  if (wave <= 66) return 840 + (wave - WAVE_AT_14_MIN) * 25;
  return 1790 + (wave - 66) * 20;
}

function getWaveComposition(wave: number) {
  const spawnTime = getWaveSpawnTime(wave);
  let hasCannon = false;
  if (wave >= 3) {
    if (wave <= WAVE_AT_14_MIN) hasCannon = wave % 3 === 0;
    else if (wave >= 55) hasCannon = true;
    else hasCannon = wave % 2 === 1;
  }
  let melee = 3, caster = 3;
  const cannon = hasCannon ? 1 : 0;
  if (spawnTime >= 840 && hasCannon) melee = 2;
  if (spawnTime >= 1800) caster = 2;
  return { melee, caster, cannon };
}

function getLevelFromXP(xp: number, maxLevel: number): number {
  for (let lvl = Math.min(maxLevel, 20); lvl >= 1; lvl--) {
    if (xp >= LEVEL_XP_TABLE[lvl]) return lvl;
  }
  return 1;
}

/** Fractional level (e.g. 7.3 = 30% of the way from lv 7 to lv 8 in XP terms) */
function fractionalLevel(xp: number, maxLevel: number): number {
  const lvl = getLevelFromXP(xp, maxLevel);
  if (lvl >= maxLevel || lvl >= 20) return lvl;
  const cur = LEVEL_XP_TABLE[lvl];
  const next = LEVEL_XP_TABLE[lvl + 1];
  const span = next - cur;
  return span > 0 ? lvl + (xp - cur) / span : lvl;
}

/** Level at a given game time — includes top lane quest XP bonus */
function getLevelAtGameTime(gameTimeSec: number, role: Role): number {
  const xpMult = getXPMult(role);
  const maxLvl = role === 'top' ? 20 : 18;
  const isTop = role === 'top';
  let cumXP = 0;
  let questBonusApplied = false;

  for (let w = 1; w <= MAX_WAVES; w++) {
    const clearSec = getWaveSpawnTime(w) + 30;
    if (clearSec > gameTimeSec) break;
    const comp = getWaveComposition(w);
    let waveXP = Math.round(
      (BASE_XP.melee * comp.melee +
        BASE_XP.caster * comp.caster +
        BASE_XP.cannon * comp.cannon) * xpMult
    );
    if (isTop && clearSec >= TOP_QUEST_COMPLETION_SEC) {
      if (!questBonusApplied) {
        cumXP += TOP_QUEST_BONUS_XP;
        questBonusApplied = true;
      }
      waveXP = Math.round(waveXP * (1 + TOP_QUEST_BONUS_XP_PCT));
    }
    cumXP += waveXP;
  }
  return getLevelFromXP(cumXP, maxLvl);
}

/** Full XP progression from 0 → maxSec for a given lane type */
function computeXPProgression(role: Role, maxSec: number = 1500, step: number = 5): XPDataPoint[] {
  const xpMult = getXPMult(role);
  const maxLvl = role === 'top' ? 20 : 18;
  const isTop = role === 'top';
  const points: XPDataPoint[] = [];

  let cumXP = 0;
  let nextWave = 1;
  let questBonusApplied = false;

  for (let sec = 0; sec <= maxSec; sec += step) {
    while (nextWave <= MAX_WAVES) {
      const clearSec = getWaveSpawnTime(nextWave) + 30;
      if (clearSec > sec) break;

      const comp = getWaveComposition(nextWave);
      let waveXP = Math.round(
        (BASE_XP.melee * comp.melee +
          BASE_XP.caster * comp.caster +
          BASE_XP.cannon * comp.cannon) * xpMult
      );

      if (isTop && clearSec >= TOP_QUEST_COMPLETION_SEC) {
        if (!questBonusApplied) {
          cumXP += TOP_QUEST_BONUS_XP;
          questBonusApplied = true;
        }
        waveXP = Math.round(waveXP * (1 + TOP_QUEST_BONUS_XP_PCT));
      }
      cumXP += waveXP;
      nextWave++;
    }

    points.push({
      sec,
      xp: cumXP,
      level: getLevelFromXP(cumXP, maxLvl),
      fractionalLevel: fractionalLevel(cumXP, maxLvl),
    });
  }
  return points;
}

// ── Gold helpers ──

function calcCSGold(cspm: number, tMin: number): number {
  const ft = Math.max(0, tMin - 1.5);
  if (ft <= 0) return 0;
  let total = 0;
  let rem = ft;
  let cur = 1.5;
  const segments: [number, number][] = [[15, 19.8], [17.75, 22.6], [25, 23.0]];
  for (const [end, rate] of segments) {
    if (rem <= 0) break;
    if (cur < end) {
      const dt = Math.min(rem, end - cur);
      total += cspm * dt * rate;
      rem -= dt;
      cur += dt;
    }
  }
  if (rem > 0) total += cspm * rem * 27.6;
  return total;
}

function calcPassiveGold(tMin: number): number {
  const tSec = tMin * 60;
  return tSec > 110 ? ((tSec - 110) / 10) * 20.4 : 0;
}

function totalGoldAtTime(tSec: number, extraGold: number = 0): number {
  const tMin = tSec / 60;
  return STARTING_GOLD + calcPassiveGold(tMin) + calcCSGold(DEFAULT_CSPM, tMin) + extraGold;
}

function findTimeForGold(targetGold: number, extraGold: number = 0): number {
  if (totalGoldAtTime(0, extraGold) >= targetGold) return 0;
  let lo = 0, hi = 2400;
  for (let i = 0; i < 50; i++) {
    const mid = (lo + hi) / 2;
    if (totalGoldAtTime(mid, extraGold) < targetGold) lo = mid;
    else hi = mid;
  }
  return Math.round((lo + hi) / 2);
}

function secondsToMMSS(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

function getItemCost(itemId: string, itemsMap: ItemsMap): number {
  return itemsMap[itemId]?.gold?.total ?? 0;
}

// ═══════════════════════════════════════════════════════════════════
// Build processing — purchase order & spike extraction
// ═══════════════════════════════════════════════════════════════════

/** One step in the planned purchase order */
interface PurchaseStep {
  id: string;
  cumulativeCost: number;
  isStarter?: boolean;
  isBootsT1?: boolean;
  isBootsT2?: boolean;
}

/**
 * Build the full purchase order for a champion: starter → boots T1 → core1
 * → boots T2 → core2 → core3.  Each step carries the cumulative gold needed
 * to have completed everything up to and including it.
 */
function buildPurchaseOrder(
  build: ChampionItemBuildData,
  role: Role,
  itemsMap: ItemsMap,
): PurchaseStep[] {
  const isSup = role === 'sup';
  const isJgl = role === 'jgl';
  const cost = (id: string) => getItemCost(id, itemsMap);

  // ── Starter ──
  let starterId: string;
  if (isSup) starterId = '3865';
  else if (isJgl) {
    const db = build.starter?.item_id?.toString();
    starterId = db && JUNGLE_STARTER_IDS.has(db) ? db : '1101';
  } else {
    const db = build.starter?.item_id?.toString();
    starterId = (db && !JUNGLE_STARTER_IDS.has(db)) ? db : '1055';
  }

  // ── Core items (non-boots, non-support) ──
  const coreRaw = [
    build.coreItems.item1_id.toString(),
    build.coreItems.item2_id.toString(),
    build.coreItems.item3_id.toString(),
  ];
  const coreIds: string[] = [];
  const seen = new Set<string>();
  for (const id of coreRaw) {
    if (seen.has(id) || BOOTS_IDS.has(id)) continue;
    if (isSup && ALL_SUPPORT_ITEM_IDS.has(id)) continue;
    coreIds.push(id);
    seen.add(id);
  }
  if (coreIds.length < 3 && build.supplementaryItems) {
    for (const it of build.supplementaryItems) {
      if (coreIds.length >= 3) break;
      const id = it.item_id.toString();
      if (seen.has(id) || BOOTS_IDS.has(id)) continue;
      if (isSup && ALL_SUPPORT_ITEM_IDS.has(id)) continue;
      coreIds.push(id);
      seen.add(id);
    }
  }

  // ── Boots T2 ──
  let bootsT2Id: string | null = null;
  for (const id of coreRaw) {
    if (BOOTS_IDS.has(id) && id !== '1001') { bootsT2Id = id; break; }
  }
  if (!bootsT2Id && build.boots?.item_id) bootsT2Id = build.boots.item_id.toString();

  // ── Build sequential purchase list ──
  const steps: PurchaseStep[] = [];
  let cum = cost(starterId);

  steps.push({ id: starterId, cumulativeCost: cum, isStarter: true });

  cum += 300; // boots T1
  steps.push({ id: '1001', cumulativeCost: cum, isBootsT1: true });

  if (coreIds[0]) { cum += cost(coreIds[0]); steps.push({ id: coreIds[0], cumulativeCost: cum }); }

  if (bootsT2Id) {
    const upgradeCost = Math.max(0, cost(bootsT2Id) - 300);
    cum += upgradeCost;
    steps.push({ id: bootsT2Id, cumulativeCost: cum, isBootsT2: true });
  }

  if (coreIds[1]) { cum += cost(coreIds[1]); steps.push({ id: coreIds[1], cumulativeCost: cum }); }
  if (coreIds[2]) { cum += cost(coreIds[2]); steps.push({ id: coreIds[2], cumulativeCost: cum }); }

  return steps;
}

/** Given a purchase order and available gold, return which items the champion has in inventory. */
function getDisplayItemsAtGold(steps: PurchaseStep[], goldAvailable: number): string[] {
  const completed = steps.filter(s => s.cumulativeCost <= goldAvailable);
  const hasBootsT2 = completed.some(s => s.isBootsT2);

  return completed
    .filter(s => {
      if (s.isBootsT1 && hasBootsT2) return false; // T2 replaces T1
      return true;
    })
    .map(s => s.id);
}

/** Legacy spike extraction — reuses the purchase order logic. */
function extractSpikeData(
  build: ChampionItemBuildData,
  tier: 1 | 2 | 3,
  role: Role,
  itemsMap: ItemsMap,
): { items: string[]; totalCost: number } {
  const steps = buildPurchaseOrder(build, role, itemsMap);

  // Determine which steps belong to each tier:
  //   Tier 1: starter + boots T1 + core1
  //   Tier 2: starter + boots T2 + core1 + core2
  //   Tier 3: starter + boots T2 + core1 + core2 + core3
  // The cumulative cost of the last step in the tier = totalCost
  const coreSteps = steps.filter(s => !s.isStarter && !s.isBootsT1 && !s.isBootsT2);
  const bootsT2Step = steps.find(s => s.isBootsT2);

  let targetStepIdx: number;
  if (tier === 1) {
    // Up through first core item (starter + bootsT1 + core1)
    const core1 = coreSteps[0];
    targetStepIdx = core1 ? steps.indexOf(core1) : steps.length - 1;
  } else if (tier === 2) {
    // Up through second core item (need boots T2 + core2)
    const core2 = coreSteps[1];
    targetStepIdx = core2 ? steps.indexOf(core2) : steps.length - 1;
  } else {
    const core3 = coreSteps[2];
    targetStepIdx = core3 ? steps.indexOf(core3) : steps.length - 1;
  }

  targetStepIdx = Math.min(targetStepIdx, steps.length - 1);
  const included = steps.slice(0, targetStepIdx + 1);
  const totalCost = included[included.length - 1]?.cumulativeCost ?? 0;

  // For display: exclude starter, replace bootsT1 with bootsT2 if present
  const hasBootsT2InSlice = included.some(s => s.isBootsT2);
  const items = included
    .filter(s => {
      if (s.isStarter) return false;
      if (s.isBootsT1 && (hasBootsT2InSlice || (tier >= 2 && bootsT2Step))) return false;
      return true;
    })
    .map(s => s.id);

  // For tier 2/3, if bootsT2 isn't included yet in the slice but is earlier than core2,
  // add it — it should already be there via the sequential order.
  if (tier >= 2 && bootsT2Step && !items.includes(bootsT2Step.id)) {
    items.unshift(bootsT2Step.id);
  }

  return { items, totalCost };
}

// ═══════════════════════════════════════════════════════════════════
// Drake timestamp generation
// ═══════════════════════════════════════════════════════════════════

function generateDrakeEvents(firstDrakeMin: number): GameEvent[] {
  const events: GameEvent[] = [];
  for (let i = 0; i < MAX_DRAKES; i++) {
    const min = firstDrakeMin + i * (DRAKE_RESPAWN_SEC / 60);
    const sec = Math.round(min * 60);
    events.push({
      key: `drake${i + 1}`,
      label: `D${i + 1}`,
      sub: secondsToMMSS(sec),
      sec,
    });
  }
  return events;
}

function buildKeyTimes(drakeEvents: GameEvent[]): GameEvent[] {
  return [...drakeEvents, ...FIXED_EVENTS].sort((a, b) => a.sec - b.sec);
}

// ═══════════════════════════════════════════════════════════════════
// XP Graph — lane comparison curves
// ═══════════════════════════════════════════════════════════════════

const LANE_CURVES = [
  { key: 'top', label: 'Top (Solo + Quest)', color: '#c9aa71', role: 'top' as Role },
  { key: 'mid', label: 'Mid / Jgl (Solo)', color: '#a0aec0', role: 'mid' as Role },
  { key: 'duo', label: 'Bot / Support (Duo)', color: '#4a9eff', role: 'bot' as Role },
];

const CHART_W = 700;
const CHART_H = 320;
const PAD = { top: 20, right: 25, bottom: 50, left: 48 };
const PLOT_W = CHART_W - PAD.left - PAD.right;
const PLOT_H = CHART_H - PAD.top - PAD.bottom;
const MAX_CHART_SEC = 25 * 60;
const MAX_CHART_LVL = 20;

const xScale = (sec: number) => PAD.left + (sec / MAX_CHART_SEC) * PLOT_W;
const yScale = (lvl: number) => PAD.top + PLOT_H - ((lvl - 1) / (MAX_CHART_LVL - 1)) * PLOT_H;

// ═══════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════

interface Props { blueTeam: TeamSlot[]; redTeam: TeamSlot[]; }

const ROLE_LABELS: Record<Role, string> = {
  top: 'Top', jgl: 'Jungle', mid: 'Mid', bot: 'Bot', sup: 'Support',
};

const TeamCompBenchmarks: React.FC<Props> = ({ blueTeam, redTeam }) => {
  const [apiVersion, setApiVersion] = useState('15.9.1');
  const [itemsMap, setItemsMap] = useState<ItemsMap>({});
  const [buildData, setBuildData] = useState<Record<string, ChampionItemBuildData | null>>({});
  const [buildLoading, setBuildLoading] = useState<Record<string, boolean>>({});
  const [goldDiffs, setGoldDiffs] = useState<Record<Role, number>>({
    top: 0, jgl: 0, mid: 0, bot: 0, sup: 0,
  });
  const [firstDrakeMin, setFirstDrakeMin] = useState(5);
  const [selectedTimestamp, setSelectedTimestamp] = useState<GameEvent | null>(null);

  // ── Dynamic drake + event timestamps ──
  const drakeEvents = useMemo(() => generateDrakeEvents(firstDrakeMin), [firstDrakeMin]);
  const keyTimes = useMemo(() => buildKeyTimes(drakeEvents), [drakeEvents]);

  // ── Fetch API version + DDragon items ──
  useEffect(() => {
    DataService.fetchLatestVersion()
      .then(ver => {
        setApiVersion(ver);
        return fetch(`https://ddragon.leagueoflegends.com/cdn/${ver}/data/en_US/item.json`);
      })
      .then(r => r.json())
      .then(data => setItemsMap(data.data ?? {}))
      .catch(() => {});
  }, []);

  // ── All unique champion IDs ──
  const allChampIds = useMemo(() => {
    const ids = new Set<string>();
    [...blueTeam, ...redTeam].forEach(s => { if (s.championId) ids.add(s.championId); });
    return Array.from(ids);
  }, [blueTeam, redTeam]);

  // ── Fetch item-build data ──
  useEffect(() => {
    const base = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    allChampIds.forEach(id => {
      if (buildData[id] !== undefined || buildLoading[id]) return;
      setBuildLoading(prev => ({ ...prev, [id]: true }));
      fetch(`${base}/api/stats/champion/${id}/item-build`)
        .then(r => (r.ok ? r.json() : null))
        .catch(() => null)
        .then((data: ChampionItemBuildData | null) => {
          setBuildData(prev => ({ ...prev, [id]: data }));
          setBuildLoading(prev => ({ ...prev, [id]: false }));
        });
    });
  }, [allChampIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Image helpers ──
  const champImg = (id: string) =>
    `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/champion/${id}.png`;
  const itemImg = (id: string) =>
    `https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/${id}.png`;

  // ── Compute spike info ──
  const getSpikeInfo = (
    champId: string | null,
    role: Role,
    tier: 1 | 2 | 3,
    extraGold: number,
  ): SpikeInfo | null => {
    if (!champId) return null;
    const build = buildData[champId];
    if (!build || Object.keys(itemsMap).length === 0) return null;
    const { items, totalCost } = extractSpikeData(build, tier, role, itemsMap);
    const timeSec = findTimeForGold(totalCost, extraGold);
    const level = getLevelAtGameTime(timeSec, role);
    return { time: secondsToMMSS(timeSec), timeSec, level, goldNeeded: totalCost, items };
  };

  // ── XP graph progressions (constant — doesn't depend on champion picks) ──
  const xpProgressions = useMemo(() => {
    return LANE_CURVES.map(c => ({
      ...c,
      data: computeXPProgression(c.role, MAX_CHART_SEC),
    }));
  }, []);

  // ── Compute snapshot data for the selected timestamp ──
  const snapshotData: SnapshotChampionData[] = useMemo(() => {
    if (!selectedTimestamp) return [];

    const roles: Role[] = ['top', 'jgl', 'mid', 'bot', 'sup'];
    const result: SnapshotChampionData[] = [];
    const hasItems = Object.keys(itemsMap).length > 0;

    roles.forEach((role, i) => {
      const blueSlot = blueTeam[i];
      const redSlot = redTeam[i];
      const diff = goldDiffs[role];
      const blueExtra = Math.max(0, diff);
      const redExtra = Math.max(0, -diff);

      if (blueSlot.championId) {
        const level = getLevelAtGameTime(selectedTimestamp.sec, role);
        const gold = totalGoldAtTime(selectedTimestamp.sec, blueExtra);
        const po = blueSlot.championId && buildData[blueSlot.championId] && hasItems
          ? buildPurchaseOrder(buildData[blueSlot.championId]!, role, itemsMap)
          : null;
        const items = po ? getDisplayItemsAtGold(po, gold) : [];

        result.push({
          championId: blueSlot.championId,
          championName: blueSlot.championName || blueSlot.championId,
          role,
          team: 'blue',
          level,
          items,
        });
      }

      if (redSlot.championId) {
        const level = getLevelAtGameTime(selectedTimestamp.sec, role);
        const gold = totalGoldAtTime(selectedTimestamp.sec, redExtra);
        const po = redSlot.championId && buildData[redSlot.championId] && hasItems
          ? buildPurchaseOrder(buildData[redSlot.championId]!, role, itemsMap)
          : null;
        const items = po ? getDisplayItemsAtGold(po, gold) : [];

        result.push({
          championId: redSlot.championId,
          championName: redSlot.championName || redSlot.championId,
          role,
          team: 'red',
          level,
          items,
        });
      }
    });

    return result;
  }, [selectedTimestamp, blueTeam, redTeam, goldDiffs, buildData, itemsMap]);

  const handleCloseSnapshot = useCallback(() => setSelectedTimestamp(null), []);

  // ═══════════════════════════════════════════════════════════════
  // Render helpers — table
  // ═══════════════════════════════════════════════════════════════

  const renderChampCell = (slot: TeamSlot, team: 'blue' | 'red') => {
    if (!slot.championId) {
      return (
        <td className="tcb-bench-td-champ">
          <div className="tcb-bench-champ-cell empty">
            <div className="tcb-bench-empty-circle" />
            <span className="tcb-bench-empty-label">Empty</span>
          </div>
        </td>
      );
    }
    return (
      <td className="tcb-bench-td-champ">
        <div className={`tcb-bench-champ-cell ${team}`}>
          <img src={champImg(slot.championId)} alt="" className="tcb-bench-champ-img" />
          <span className="tcb-bench-champ-name">
            {slot.championName || slot.championId}
          </span>
        </div>
      </td>
    );
  };

  const renderTimestampCell = (
    gameTimeSec: number,
    role: Role,
    purchaseOrder: PurchaseStep[] | null,
    extraGold: number,
  ) => {
    const level = getLevelAtGameTime(gameTimeSec, role);
    const gold = totalGoldAtTime(gameTimeSec, extraGold);
    const displayItems = purchaseOrder ? getDisplayItemsAtGold(purchaseOrder, gold) : [];

    return (
      <td className="tcb-bench-td-level">
        <div className="tcb-bench-ts-cell">
          <span className="tcb-bench-level-num">{level}</span>
          {displayItems.length > 0 && (
            <div className="tcb-bench-ts-items">
              {displayItems.map((id, idx) => (
                <img
                  key={idx}
                  src={itemImg(id)}
                  alt=""
                  className="tcb-bench-ts-item-img"
                  title={itemsMap[id]?.name || id}
                />
              ))}
            </div>
          )}
        </div>
      </td>
    );
  };

  const renderSpikeCell = (
    spike: SpikeInfo | null,
    oppSpike: SpikeInfo | null,
    isLoading: boolean,
  ) => {
    if (isLoading) return <td className="tcb-bench-td-spike loading"><span className="tcb-bench-loading-text">…</span></td>;
    if (!spike) return <td className="tcb-bench-td-spike empty"><span className="tcb-bench-no-data">—</span></td>;

    let advClass = '';
    if (oppSpike) {
      const d = spike.timeSec - oppSpike.timeSec;
      if (d < -20) advClass = 'ahead';
      else if (d > 20) advClass = 'behind';
    }

    return (
      <td className={`tcb-bench-td-spike ${advClass}`}>
        <div className="tcb-bench-spike-cell">
          <div className="tcb-bench-spike-timing">
            <span className="tcb-bench-spike-time">{spike.time}</span>
            <span className="tcb-bench-spike-level">Lv {spike.level}</span>
          </div>
          <div className="tcb-bench-spike-items">
            {spike.items.map((id, idx) => (
              <img key={idx} src={itemImg(id)} alt="" className="tcb-bench-spike-item-img" />
            ))}
          </div>
          <div className="tcb-bench-spike-gold">{spike.goldNeeded.toLocaleString()}g</div>
        </div>
      </td>
    );
  };

  const renderChampRow = (
    slot: TeamSlot, team: 'blue' | 'red', role: Role,
    mySpikes: (SpikeInfo | null)[], oppSpikes: (SpikeInfo | null)[],
    events: GameEvent[],
    extraGold: number,
  ) => {
    const loading = slot.championId ? !!buildLoading[slot.championId] : false;
    const purchaseOrder = slot.championId && buildData[slot.championId] && Object.keys(itemsMap).length > 0
      ? buildPurchaseOrder(buildData[slot.championId]!, role, itemsMap)
      : null;

    return (
      <tr className={`tcb-bench-champ-row ${team}`}>
        {renderChampCell(slot, team)}
        {events.map(kt => (
          <React.Fragment key={kt.key}>
            {renderTimestampCell(kt.sec, role, purchaseOrder, extraGold)}
          </React.Fragment>
        ))}
        {[0, 1, 2].map(i => renderSpikeCell(mySpikes[i], oppSpikes[i], loading))}
      </tr>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // Render helpers — XP graph (SVG)
  // ═══════════════════════════════════════════════════════════════

  const renderXPGraph = () => {
    const gridLevels = [2, 4, 6, 8, 10, 12, 14, 16, 18, 20];
    const highlightLevels = new Set([6, 11, 16]);
    const timeMarkers = [0, 5, 10, 15, 20, 25];

    // Quest completion marker
    const questSec = TOP_QUEST_COMPLETION_SEC;
    const questData = xpProgressions.find(p => p.key === 'top')?.data;
    const questPoint = questData?.find(d => d.sec >= questSec);

    // Only show events within the chart range
    const chartEvents = keyTimes.filter(kt => kt.sec <= MAX_CHART_SEC);

    return (
      <div className="tcb-xp-graph-section">
        <h3 className="tcb-xp-graph-title">XP &amp; Level Comparison by Lane</h3>
        <p className="tcb-xp-graph-subtitle">
          Solo (top) with quest bonus vs Solo (mid/jgl) vs Duo (bot/sup) · {DEFAULT_CSPM} CS/min · No kills
        </p>
        <p className="tcb-xp-graph-quest-note">
          Top quest completes ~{secondsToMMSS(TOP_QUEST_COMPLETION_SEC)} → +{TOP_QUEST_BONUS_XP} XP, +{TOP_QUEST_BONUS_XP_PCT * 100}% minion XP, level cap → 20
        </p>

        <div className="tcb-xp-chart-container">
          <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} className="tcb-xp-chart" preserveAspectRatio="xMidYMid meet">
            {/* ── Grid lines (horizontal = levels) ── */}
            {gridLevels.map(lvl => {
              if (lvl > MAX_CHART_LVL) return null;
              const y = yScale(lvl);
              const isHighlight = highlightLevels.has(lvl);
              return (
                <g key={`grid-${lvl}`}>
                  <line
                    x1={PAD.left} y1={y} x2={CHART_W - PAD.right} y2={y}
                    stroke={isHighlight ? 'rgba(201,170,113,0.25)' : 'rgba(255,255,255,0.06)'}
                    strokeWidth={isHighlight ? 1 : 0.5}
                    strokeDasharray={isHighlight ? '' : '3,3'}
                  />
                  <text
                    x={PAD.left - 8} y={y + 4}
                    textAnchor="end"
                    fill={isHighlight ? '#c9aa71' : '#555'}
                    fontSize={isHighlight ? 11 : 9}
                    fontWeight={isHighlight ? 600 : 400}
                  >
                    {lvl}
                  </text>
                </g>
              );
            })}

            {/* ── Vertical time markers ── */}
            {timeMarkers.map(min => {
              const x = xScale(min * 60);
              return (
                <g key={`time-${min}`}>
                  <line
                    x1={x} y1={PAD.top} x2={x} y2={CHART_H - PAD.bottom}
                    stroke="rgba(255,255,255,0.06)"
                    strokeWidth={0.5}
                  />
                  <text
                    x={x} y={CHART_H - PAD.bottom + 16}
                    textAnchor="middle"
                    fill="#6a7a8e"
                    fontSize={10}
                  >
                    {min}:00
                  </text>
                </g>
              );
            })}

            {/* ── Key event markers ── */}
            {chartEvents.map(kt => {
              const x = xScale(kt.sec);
              const isDrake = kt.key.startsWith('drake');
              return (
                <g key={`evt-${kt.key}`}>
                  <line
                    x1={x} y1={PAD.top} x2={x} y2={CHART_H - PAD.bottom}
                    stroke={isDrake ? 'rgba(100,160,255,0.15)' : 'rgba(201,170,113,0.15)'}
                    strokeWidth={1}
                    strokeDasharray="4,4"
                  />
                  <text
                    x={x} y={CHART_H - PAD.bottom + 30}
                    textAnchor="middle"
                    fill={isDrake ? '#6a9eff' : '#c9aa71'}
                    fontSize={isDrake ? 8 : 9}
                    fontWeight={600}
                  >
                    {kt.label}
                  </text>
                </g>
              );
            })}

            {/* ── XP curves ── */}
            {xpProgressions.map(curve => {
              const pts = curve.data
                .map(d => `${xScale(d.sec)},${yScale(d.fractionalLevel)}`)
                .join(' ');
              return (
                <polyline
                  key={curve.key}
                  points={pts}
                  fill="none"
                  stroke={curve.color}
                  strokeWidth={2.5}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  opacity={0.9}
                />
              );
            })}

            {/* ── Quest completion marker ── */}
            {questPoint && (
              <g>
                <circle
                  cx={xScale(questSec)}
                  cy={yScale(questPoint.fractionalLevel)}
                  r={5}
                  fill="#c9aa71"
                  stroke="#0d0f14"
                  strokeWidth={2}
                />
                <text
                  x={xScale(questSec) + 8}
                  y={yScale(questPoint.fractionalLevel) - 8}
                  fill="#c9aa71"
                  fontSize={9}
                  fontWeight={600}
                >
                  Quest ✓
                </text>
              </g>
            )}

            {/* ── Axis labels ── */}
            <text
              x={CHART_W / 2}
              y={CHART_H - 4}
              textAnchor="middle"
              fill="#6a7a8e"
              fontSize={11}
            >
              Game Time
            </text>
            <text
              x={12}
              y={CHART_H / 2}
              textAnchor="middle"
              fill="#6a7a8e"
              fontSize={11}
              transform={`rotate(-90, 12, ${CHART_H / 2})`}
            >
              Level
            </text>
          </svg>

          {/* ── Legend ── */}
          <div className="tcb-xp-legend">
            {LANE_CURVES.map(c => (
              <div key={c.key} className="tcb-xp-legend-item">
                <span className="tcb-xp-legend-swatch" style={{ background: c.color }} />
                <span className="tcb-xp-legend-label">{c.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // Main render
  // ═══════════════════════════════════════════════════════════════

  const hasAny = allChampIds.length > 0;
  const totalCols = 1 + keyTimes.length + 3;

  return (
    <div className="tcb-bench-section">
      <h2 className="tcb-bench-title">Benchmarks</h2>
      <p className="tcb-bench-subtitle">
        Levels &amp; item power spikes · Default {DEFAULT_CSPM} CS/min · No kills
      </p>

      {/* ── Drake timer control ── */}
      <div className="tcb-drake-timer-row">
        <label className="tcb-drake-timer-label">
          First drake taken at
        </label>
        <input
          type="number"
          className="tcb-drake-timer-input"
          value={firstDrakeMin}
          min={5}
          max={20}
          step={0.5}
          onChange={e => {
            const v = parseFloat(e.target.value);
            if (!isNaN(v) && v >= 5 && v <= 20) setFirstDrakeMin(v);
          }}
        />
        <span className="tcb-drake-timer-unit">min</span>
        <span className="tcb-drake-timer-info">
          (each subsequent drake +{DRAKE_RESPAWN_SEC / 60} min)
        </span>
      </div>

      {/* ── XP Graph (always visible) ── */}
      {renderXPGraph()}

      {/* ── Table (only if champions are picked) ── */}
      {hasAny && (
        <div className="tcb-bench-table-wrap">
          <table className="tcb-bench-table">
            <thead>
              <tr>
                <th className="tcb-bench-th tcb-bench-th-champ">Champion</th>
                {keyTimes.map(kt => {
                  const isDrake = kt.key.startsWith('drake');
                  const isSelected = selectedTimestamp?.key === kt.key;
                  return (
                    <th
                      key={kt.key}
                      className={`tcb-bench-th tcb-bench-th-level ${isDrake ? 'drake' : ''} ${isSelected ? 'selected' : ''}`}
                      onClick={() => setSelectedTimestamp(prev => prev?.key === kt.key ? null : kt)}
                      style={{ cursor: 'pointer' }}
                      title="Click to open snapshot at this timestamp"
                    >
                      <div className="tcb-bench-th-label">{kt.label}</div>
                      <div className="tcb-bench-th-sub">{kt.sub}</div>
                      {isSelected && <div className="tcb-bench-th-selected-dot">▼</div>}
                    </th>
                  );
                })}
                {[1, 2, 3].map(tier => (
                  <th key={`spike-${tier}`} className="tcb-bench-th tcb-bench-th-spike">
                    <div className="tcb-bench-th-label">{tier} Item{tier > 1 ? 's' : ''}</div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {blueTeam.map((blueSlot, i) => {
                const redSlot = redTeam[i];
                const role = blueSlot.role;
                const diff = goldDiffs[role];
                const blueExtra = Math.max(0, diff);
                const redExtra = Math.max(0, -diff);

                const blueSpikes = ([1, 2, 3] as const).map(t =>
                  getSpikeInfo(blueSlot.championId, role, t, blueExtra)
                );
                const redSpikes = ([1, 2, 3] as const).map(t =>
                  getSpikeInfo(redSlot.championId, role, t, redExtra)
                );

                return (
                  <React.Fragment key={role}>
                    <tr className="tcb-bench-role-row">
                      <td colSpan={totalCols}>
                        <div className="tcb-bench-role-header">
                          <img src={blueSlot.icon} alt="" className="tcb-bench-role-icon" />
                          <span className="tcb-bench-role-name">{ROLE_LABELS[role]}</span>
                          <select
                            value={diff}
                            onChange={e => setGoldDiffs(prev => ({ ...prev, [role]: Number(e.target.value) }))}
                            className="tcb-bench-gold-select"
                          >
                            {GOLD_DIFF_OPTIONS.map(v => (
                              <option key={v} value={v}>
                                {v === 0 ? 'Even' : v > 0 ? `Blue +${v}g` : `Red +${Math.abs(v)}g`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                    </tr>
                    {renderChampRow(blueSlot, 'blue', role, blueSpikes, redSpikes, keyTimes, blueExtra)}
                    {renderChampRow(redSlot, 'red', role, redSpikes, blueSpikes, keyTimes, redExtra)}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Snapshot view (when a timestamp is selected) ── */}
      {selectedTimestamp && snapshotData.length > 0 && (
        <TeamCompSnapshot
          timestamp={selectedTimestamp}
          champions={snapshotData}
          apiVersion={apiVersion}
          onClose={handleCloseSnapshot}
        />
      )}
    </div>
  );
};

export default TeamCompBenchmarks;
